import { combineReducers, Reducer, configureStore } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { thunk } from 'redux-thunk';
import { persistStore, persistReducer, PersistConfig } from 'redux-persist';
import syncStateMiddleware from './middleware/syncStateMiddleware';
import coreScriptReducer, {
  CoreScriptState,
} from './reducer/coreScriptReducer';
import galleryReducer from './reducer/galleryReducer';
import contextIdReducer, {
  ContextIdStateProps,
} from './reducer/contextIdReducer';
import languageReducer, { LanguageStateType } from './reducer/languageReducer';
import selectedQuestionReducer from './reducer/selectedQuestionReducer';
import inspactOrderReducer from './reducer/inspact/order_reducer';
import { storage } from './storage';
import { PersistHelper } from './persistHelper';
import { Validation } from '../utils';
import permissionReducer from './reducer/permissionReducer';
import forbiddenDetectorReducer from './reducer/forbiddenDetectorReducer';
import loadingReducer from './reducer/loadingReducer';
function createPersistedReducer<T>(
  reducer: Reducer<T>,
  key: string
): Reducer<T> {
  if (!Validation.isPersistEnabled()) {
    return reducer;
  }

  const persistConfig: PersistConfig<T> = {
    key,
    storage,
  };

  return persistReducer(persistConfig, reducer) as any; // Persisted reducer with the same type
}

// Define the root reducer with specific persisted states
const rootReducer = combineReducers({
  language: createPersistedReducer<LanguageStateType>(
    languageReducer,
    PersistHelper.PersistKeys.LANGUAGE
  ),
  coreScript: createPersistedReducer<CoreScriptState>(
    coreScriptReducer,
    PersistHelper.PersistKeys.CORE_SCRIPT
  ),
  contextId: createPersistedReducer<ContextIdStateProps>(
    contextIdReducer,
    PersistHelper.PersistKeys.CONTEXT_ID
  ),
  permissions: permissionReducer,
  gallery: galleryReducer,
  selectedQuestion: selectedQuestionReducer,
  inspactOrder: createPersistedReducer(
    inspactOrderReducer,
    PersistHelper.PersistKeys.INSPECT_ORDER
  ),
  forbiddenDetector: forbiddenDetectorReducer,
  loading: loadingReducer,
});

// Configure the Redux store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(
      thunk,
      syncStateMiddleware
    ),
});

// Setup persistor for state persistence
// export const persistor = persistStore(store, undefined, () => {
//   console.log('State rehydration complete.');
// });
const persistor = persistStore(store);

// Infer RootState and AppDispatch for type-safe usage
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// Custom hook for typed selectors
export const useAppSelector = <TSelected>(
  selector: (state: RootState) => TSelected
) => useSelector(selector);

export { store, persistor };
